import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Calendar, FileBarChart, Mail, Search, Users as UsersIcon } from 'lucide-react';
import { api, type ReportConfig, type ReportRun } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { SortableHeader } from '../components/ui/SortableHeader';
import { useSort } from '../lib/sort';
import { formatRelative } from '../lib/format';
import { deriveStatus, DISPLAY_LABEL, DISPLAY_VARIANT, humanizeSchedule } from '../lib/reports';

type EnrichedConfig = ReportConfig & { lastRun: ReportRun | undefined };

const getConfigValue = (item: EnrichedConfig, key: string): unknown => {
  switch (key) {
    case 'report_id':
      return item.report_id;
    case 'report_name':
      return item.report_name ?? '';
    case 'client':
      return item.client ?? '';
    case 'enabled':
      return item.enabled === true ? 1 : 0;
    case 'schedule':
      return humanizeSchedule(item.schedule);
    case 'recipients':
      return item.recipients?.length ?? 0;
    case 'lastRun':
      return item.lastRun?.run_timestamp ?? '';
    case 'lastStatus':
      return item.lastRun ? deriveStatus(item.lastRun) : '';
    default:
      return undefined;
  }
};

export function Reporting() {
  const configs = useQuery({
    queryKey: ['reports', 'configs'],
    queryFn: () => api.listReportConfigs(),
    staleTime: 5 * 60 * 1000,
  });
  const runs = useQuery({
    queryKey: ['reports', 'runs'],
    queryFn: () => api.listReportRuns(),
    staleTime: 60 * 1000,
  });

  const [q, setQ] = useState('');

  const lastRunByReport = useMemo(() => {
    const map = new Map<string, ReportRun>();
    for (const r of runs.data?.items ?? []) {
      const prev = map.get(r.report_id);
      if (!prev || prev.run_timestamp < r.run_timestamp) map.set(r.report_id, r);
    }
    return map;
  }, [runs.data]);

  const enriched: EnrichedConfig[] = useMemo(
    () =>
      (configs.data?.items ?? []).map((c) => ({
        ...c,
        lastRun: lastRunByReport.get(c.report_id),
      })),
    [configs.data, lastRunByReport],
  );

  const filtered = useMemo(() => {
    if (!q.trim()) return enriched;
    const needle = q.toLowerCase();
    return enriched.filter(
      (c) =>
        c.report_id.toLowerCase().includes(needle) ||
        c.report_name?.toLowerCase().includes(needle) ||
        c.client?.toLowerCase().includes(needle) ||
        humanizeSchedule(c.schedule).toLowerCase().includes(needle),
    );
  }, [enriched, q]);

  const { sortedItems, sort, toggle } = useSort(filtered, getConfigValue);

  const enabledCount = enriched.filter((c) => c.enabled === true).length;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Reports</h1>
          <p className="text-muted-foreground">
            {configs.isLoading
              ? '—'
              : `${enabledCount} enabled · ${enriched.length} total`}
          </p>
        </div>
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, client, schedule…"
            className="pl-9"
          />
        </div>
      </div>

      {configs.error && <p className="text-destructive">{(configs.error as Error).message}</p>}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 border-b border-border">
              <tr>
                <SortableHeader
                  label="Report"
                  sortKey="report_name"
                  sort={sort}
                  onToggle={toggle}
                />
                <SortableHeader label="Client" sortKey="client" sort={sort} onToggle={toggle} />
                <SortableHeader label="Enabled" sortKey="enabled" sort={sort} onToggle={toggle} />
                <SortableHeader
                  label="Schedule"
                  sortKey="schedule"
                  sort={sort}
                  onToggle={toggle}
                />
                <SortableHeader
                  label="Recipients"
                  sortKey="recipients"
                  sort={sort}
                  onToggle={toggle}
                />
                <SortableHeader
                  label="Last run"
                  sortKey="lastRun"
                  sort={sort}
                  onToggle={toggle}
                />
                <SortableHeader
                  label="Last status"
                  sortKey="lastStatus"
                  sort={sort}
                  onToggle={toggle}
                />
              </tr>
            </thead>
            <tbody>
              {configs.isLoading && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              )}
              {sortedItems.map((c) => {
                const ds = c.lastRun ? deriveStatus(c.lastRun) : null;
                return (
                  <tr
                    key={c.report_id}
                    className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <Link
                        to={`/reporting/${encodeURIComponent(c.report_id)}`}
                        className="font-medium text-primary hover:underline"
                      >
                        {c.report_name ?? c.report_id}
                      </Link>
                      <div className="text-xs text-muted-foreground font-mono">{c.report_id}</div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{c.client ?? '—'}</td>
                    <td className="px-4 py-3">
                      {c.enabled === true ? (
                        <Badge variant="success">Yes</Badge>
                      ) : c.enabled === false ? (
                        <Badge variant="secondary">No</Badge>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {humanizeSchedule(c.schedule)}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-muted-foreground">
                      {c.recipients?.length ?? 0}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {c.lastRun ? formatRelative(c.lastRun.run_timestamp) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {ds ? <Badge variant={DISPLAY_VARIANT[ds]}>{DISPLAY_LABEL[ds]}</Badge> : '—'}
                    </td>
                  </tr>
                );
              })}
              {!configs.isLoading && sortedItems.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                    No matching reports.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <FileBarChart className="h-4 w-4 text-muted-foreground" />
            About this section
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p className="flex items-start gap-2">
            <Calendar className="h-4 w-4 mt-0.5 shrink-0" />
            Reports run daily at 5:00 AM Pacific. Each report's <code className="font-mono text-xs">schedule</code>{' '}
            value further filters which days it fires on.
          </p>
          <p className="flex items-start gap-2">
            <Mail className="h-4 w-4 mt-0.5 shrink-0" />
            Output is delivered to recipients as a styled HTML email with a CSV attachment, sent
            from <code className="font-mono text-xs">no-reply@xiltrixusa.com</code>.
          </p>
          <p className="flex items-start gap-2">
            <UsersIcon className="h-4 w-4 mt-0.5 shrink-0" />
            Click a report row to see its full configuration and recent run history.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
